import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaArrowRight,
  FaBars,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaComment,
  FaCompress,
  FaDollarSign,
  FaExpand,
  FaEye,
  FaForward,
  FaList,
  FaMinus,
  FaMusic,
  FaPause,
  FaPen,
  FaPlay,
  FaPlus,
  FaSearch,
  FaTrash,
  FaUndo,
} from 'react-icons/fa';

import TopPageNavigationBand from './TopPageNavigationBand.jsx';
import Logo from './Logo.jsx';
import VideoPlayer from './partial/stream/VideoPlayer.jsx';
import ChatSidebar from './partial/stream/ChatSidebar.jsx';
import AddToQueueModal from './partial/modal/AddToQueueModal.jsx';
import ConfirmationModal from './partial/modal/ConfirmationModal.jsx';
import LyricsAttribution from './partial/lyrics/LyricsAttribution.jsx';
import MultistreamFeedIndicator from './partial/MultistreamFeedIndicator.jsx';

import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { getCurrentUserId, isBandMember } from '../_helpers/bandUtils.js';
import { isJapanTraffic } from '../_helpers/japanTraffic.js';
import {
  accidentalsForKey,
  buildPerformanceSheet,
  parseLyricSheet,
  sheetHasSungText,
  transposeChord,
  transposeKey,
  withoutChordOnlyLines,
} from '../_helpers/lyricSheet.js';
import { LyricsVoice, lyricsKey, unavailableKey } from '../_helpers/lyricsMessages.js';
import { useLyricsImpression } from '../hooks/useLyricsImpression.js';
import { LyricsStatus, isProviderAnswer, useSongLyrics } from '../hooks/useSongLyrics.js';
import { useStreamStatus } from '../hooks/useStreamStatus.js';
import { capturePerformerEvent, completionProperties, noteBoostAlert, noteQueueEntries } from '../_helpers/performerEvents.js';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion.js';
import { useMultistreamStatus } from '../hooks/useMultistreamStatus.js';
import { isFeedDown } from '../_helpers/multistream.js';
import { useLocale, useT } from '../contexts/LocaleContext.jsx';
import { useBroadcastSurface } from '../contexts/BroadcastSurface.jsx';
import { isSafeEmbedUrl } from '../_helpers/streamSource.js';
import config from '../config.js';
import { serverMessage } from '../_helpers/errorMessage.js';
import { SongStatus } from '../_helpers/songConstants.js';

import '../styles/StreamerDashboard.css';

// ---------------------------------------------------------------------------
// Queue API
// ---------------------------------------------------------------------------

function normalizeReleaseDate(value) {
  if (!value) return null;
  const normalized = String(value).trim();
  if (!normalized || normalized.startsWith('0001-01-01')) {
    return null;
  }
  return normalized;
}

// `t` names a song the API returned without a title
// One object for the add-to-queue modal's defaults, not a new one per render.
const ADD_TO_QUEUE_DEFAULTS = Object.freeze({ Position: 0 });
/** How long a queue notice (a failed reorder) stays up. */
const QUEUE_NOTICE_MS = 6000;
// Every status useStreamStatus reports has a label (ENG-504): reconnecting
// and polling used to read "Unknown".
const WS_BADGE_KEYS = {
  connected: 'common.status.connected',
  connecting: 'streamer.metrics.connecting',
  reconnecting: 'streamer.metrics.reconnecting',
  polling: 'streamer.metrics.polling',
  disconnected: 'streamer.metrics.disconnected',
};

function formatQueueItem(item, t) {
  const statusMap = {
    [SongStatus.QUEUED]:  'queued',
    [SongStatus.PLAYING]: 'playing',
    [SongStatus.SKIPPED]: 'skipped',
    [SongStatus.PLAYED]:  'played',
  };
  return {
    id:            item.ID,
    songId:        item.song?.ID ?? null,
    position:      item.Position,
    status:        statusMap[item.Status] ?? 'queued',
    title:         item.song?.Title  || t('common.fallbacks.unknownTitle'),
    artist:        item.song?.Artist || '',
    requestedBy:   item.requested_by || null,
    donationAmount:item.donation_amount || 0,
    // The vibe of the entry's last boost (ENG-866), until it starts playing.
    boostedVibeId: item.boosted_vibe_id || null,
    key:           item.song?.Key    || null,
    tempo:         item.song?.Tempo  || null,
    capo:          item.song?.Capo   || null,
    chords:        item.song?.Chords || null,
    tabs:          item.song?.Tabs   || null,
    notes:         item.song?.Notes  || null,
    lyrics:        item.song?.Lyrics || null,
    releaseDate:   normalizeReleaseDate(item.song?.ReleaseDate || item.song?.release_date || null),
  };
}

function isActiveLiveShow(show) {
  if (!show) return false;

  const isOnline = Boolean(show.isOnline ?? show.IsOnline);
  const startedAt = show.startedAt ?? show.StartedAt;
  const endedAt = show.endedAt ?? show.EndedAt;

  return isOnline && Boolean(startedAt) && !endedAt;
}

// The band's live show, from `GET /bands/{id}/shows` ({ futureEvents, pastEvents })
function findActiveShow(shows) {
  const futureShows = Array.isArray(shows?.futureEvents) ? shows.futureEvents : [];
  const pastShows = Array.isArray(shows?.pastEvents) ? shows.pastEvents : [];
  return [...futureShows, ...pastShows].find(isActiveLiveShow) ?? null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDuration(startTime, now = Date.now()) {
  if (!startTime) return '0:00';
  const diff = Math.max(0, now - new Date(startTime).getTime());
  const totalSeconds = Math.floor(diff / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
}

/** "8:05 PM" in the active locale; '' when the value is missing or unparseable. */
const formatClockTime = (isoTime, format) => format.time(isoTime);

/** A tip amount in the active locale's notation ("$5", "$5.50"; see `formatUsd`). */
const formatMoney = (amount, format) => format.usd(amount);

/** How long a donation notice stays up. */
const DONATION_NOTICE_MS = 8000;
/** Longest donor name shown; the rest is cut so a pasted essay can't fill the screen. */
const DONOR_NAME_MAX = 60;
const CURRENCY_CODE = /^[A-Z]{3}$/;

/**
 * What a `donation_alert` frame shows (ENG-577), or null when it can't be
 * shown. Nothing sends one since the backend stopped receiving provider tips
 * (ENG-596); the notice stays for a future SongShake-tip frame. A frame is
 * `{ bandId, donorName, amount, currency, message, provider }`, and every
 * field is the tipper's: nothing is trusted, and it is only ever
 * rendered as text. A frame without a positive numeric amount is dropped; a
 * missing name reads as "Someone". A missing currency is taken as USD, and an
 * unrecognised one shows the bare amount rather than a guessed symbol.
 */
const donationNoticeFrom = (frame) => {
  if (!frame || typeof frame !== 'object') return null;
  const { amount } = frame;
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) return null;
  const name = typeof frame.donorName === 'string'
    ? Array.from(frame.donorName.trim()).slice(0, DONOR_NAME_MAX).join('')
    : '';
  const rawCurrency = typeof frame.currency === 'string' ? frame.currency.trim().toUpperCase() : '';
  const currency = rawCurrency === '' ? 'USD' : CURRENCY_CODE.test(rawCurrency) ? rawCurrency : null;
  return { name, amount, currency };
};

/** A donation's amount in the active locale's notation, in its own currency. */
const formatDonationAmount = ({ amount, currency }, format) => {
  if (currency === 'USD') return formatMoney(amount, format);
  if (currency) {
    try {
      return format.number(amount, { style: 'currency', currency });
    } catch {
      // An ISO-shaped code this runtime rejects: fall back to the bare amount.
    }
  }
  return format.number(amount, { maximumFractionDigits: 2 });
};

const LYRIC_FONT_SIZES = [24, 28, 32, 36, 40, 44, 48];
const DEFAULT_LYRIC_FONT_STEP = LYRIC_FONT_SIZES.indexOf(36);
const SCROLL_SPEEDS = [0.5, 1, 1.5, 2];

// ---------------------------------------------------------------------------
// Live session header pieces
// ---------------------------------------------------------------------------

const SessionMetric = ({ icon, value, label, className = '', ariaLabel }) => (
  <div
    className={`cockpit-metric ${className}`.trim()}
    role={ariaLabel ? 'status' : undefined}
    aria-label={ariaLabel}
  >
    <span className="cockpit-metric-icon" aria-hidden="true">{icon}</span>
    <span className="cockpit-metric-body">
      <span className="cockpit-metric-value">{value}</span>
      <span className="cockpit-metric-label">{label}</span>
    </span>
  </div>
);

// The design's "Menu" icon button is the header's escape hatch to normal
// navigation: the cockpit drops the band navigation bar to keep the stage
// clear, so its destinations live behind this menu instead.
const CockpitMenu = ({ items }) => {
  const t = useT();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setIsOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="cockpit-menu" ref={rootRef}>
      <button
        type="button"
        className="cockpit-icon-btn"
        onClick={() => setIsOpen(open => !open)}
        aria-label={t('streamer.menu.button')}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls="cockpit-menu-list"
        title={t('streamer.menu.button')}
      >
        <FaBars aria-hidden="true" />
      </button>
      {isOpen && (
        <div className="cockpit-menu-list" role="menu" id="cockpit-menu-list" aria-label={t('streamer.menu.list')}>
          {items.map(item => (
            <button
              key={item.labelKey}
              type="button"
              role="menuitem"
              className="cockpit-menu-item"
              onClick={() => { setIsOpen(false); item.onSelect(); }}
            >
              {t(item.labelKey)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Stream title + subline, with the inline edit form (ENG-243) in its place
const ShowInfo = ({
  bandName, startedAt, title, description, isEditing, editTitle, editDescription,
  onEdit, onSave, onCancel, onEditTitleChange, onEditDescriptionChange,
  canEdit, isSaving, saveError,
}) => {
  const { t, format } = useLocale();
  return (
    <div className="cockpit-show-info" role="region" aria-label={t('streamer.showInfo.region')}>
      {isEditing ? (
        <form
          className="cockpit-show-form"
          onSubmit={(e) => { e.preventDefault(); onSave(); }}
        >
          <input
            type="text"
            className="cockpit-input cockpit-show-form-title"
            value={editTitle}
            onChange={e => onEditTitleChange(e.target.value)}
            placeholder={t('streamer.showInfo.titleField')}
            maxLength={200}
            aria-label={t('streamer.showInfo.titleField')}
          />
          <input
            type="text"
            className="cockpit-input cockpit-show-form-desc"
            value={editDescription}
            onChange={e => onEditDescriptionChange(e.target.value)}
            placeholder={t('streamer.showInfo.descriptionField')}
            maxLength={1000}
            aria-label={t('streamer.showInfo.descriptionField')}
          />
          <button
            type="submit"
            className="cockpit-btn cockpit-btn--primary cockpit-btn--compact"
            disabled={isSaving}
          >
            {isSaving ? t('common.actions.savingEllipsis') : t('common.actions.save')}
          </button>
          <button
            type="button"
            className="cockpit-btn cockpit-btn--outline cockpit-btn--compact"
            onClick={onCancel}
            disabled={isSaving}
          >
            {t('common.actions.cancel')}
          </button>
          {saveError && (
            <span className="cockpit-show-form-error" role="alert">{t(saveError.key)}</span>
          )}
        </form>
      ) : (
        <>
          <div className="cockpit-show-title-row">
            <h1 className="cockpit-show-title">{title || '—'}</h1>
            {canEdit && (
              <button
                type="button"
                className="cockpit-show-edit-btn"
                onClick={onEdit}
                aria-label={t('streamer.showInfo.edit')}
                title={t('streamer.showInfo.edit')}
              >
                <FaPen aria-hidden="true" />
              </button>
            )}
          </div>
          <p className="cockpit-show-subline">
            <span className="cockpit-show-band">{bandName}</span>
            {startedAt && (
              <>
                <span className="cockpit-dot" aria-hidden="true">·</span>
                <span>{t('streamer.showInfo.startedAt', { time: formatClockTime(startedAt, format) })}</span>
              </>
            )}
            {description && (
              <>
                <span className="cockpit-dot" aria-hidden="true">·</span>
                <span className="cockpit-show-description">{description}</span>
              </>
            )}
          </p>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Queue rail
// ---------------------------------------------------------------------------

const DemandRow = ({ song, className = '' }) => {
  const { t, format } = useLocale();
  return (
    <div className={`cockpit-demand ${className}`.trim()}>
      {song.donationAmount > 0 && (
        <span className="cockpit-money-chip">{formatMoney(song.donationAmount, format)}</span>
      )}
      <span className="cockpit-demand-requester">
        {song.requestedBy ? t('streamer.queue.requestedBy', { name: song.requestedBy }) : t('streamer.queue.bandPick')}
      </span>
    </div>
  );
};

const ReorderControls = ({ song, canMoveUp, canMoveDown, onMove }) => {
  const t = useT();
  return (
    <div className="cockpit-reorder" aria-label={t('streamer.queue.reorder', { title: song.title })}>
      <button
        type="button"
        className="cockpit-reorder-btn"
        onClick={() => onMove(song.id, 'up')}
        disabled={!canMoveUp}
        aria-label={t('streamer.queue.moveUp', { title: song.title })}
        title={t('streamer.queue.moveUpTitle')}
      >
        <FaChevronUp aria-hidden="true" />
      </button>
      <button
        type="button"
        className="cockpit-reorder-btn"
        onClick={() => onMove(song.id, 'down')}
        disabled={!canMoveDown}
        aria-label={t('streamer.queue.moveDown', { title: song.title })}
        title={t('streamer.queue.moveDownTitle')}
      >
        <FaChevronDown aria-hidden="true" />
      </button>
    </div>
  );
};

function QueueRail({
  bandid, isLive, embedUrl, queue, searchTerm, onSearchTermChange,
  onRequeue, onMoveQueueSong, canReorderQueue, canRequeue, canAddSong, onAddSong, notice = null,
  onRemove, canRemove = false, previewSuspended = false,
}) {
  const t = useT();
  // While the preview is suspended the page still shows a broadcast, so keep
  // app-level overlays deferred as the mounted player would (ENG-836).
  useBroadcastSurface(previewSuspended && isLive && isSafeEmbedUrl(embedUrl));
  const queuedSongs = queue
    .filter(s => s.status === 'queued')
    .sort((a, b) => a.position - b.position);
  // Skipped songs are listed with the played ones, marked skipped, so they
  // can be re-queued (ENG-506); they used to vanish from the rail.
  const playedSongs = queue.filter(s => s.status === 'played' || s.status === 'skipped');

  const removeButton = (song) => canRemove && (
    <button
      type="button"
      className="cockpit-reorder-btn cockpit-remove-btn"
      onClick={() => onRemove(song)}
      title={t('streamer.queue.removeTitle')}
      aria-label={t('streamer.queue.remove', { title: song.title })}
    >
      <FaTrash aria-hidden="true" />
    </button>
  );

  const term = searchTerm.trim().toLowerCase();
  const matches = (song) =>
    !term ||
    song.title.toLowerCase().includes(term) ||
    song.artist.toLowerCase().includes(term) ||
    (song.requestedBy || '').toLowerCase().includes(term);

  const upNext = !term ? queuedSongs[0] ?? null : null;
  const listSongs = term ? queuedSongs.filter(matches) : queuedSongs.slice(1);

  return (
    <>
      <div className="cockpit-queue-header">
        <h2 className="cockpit-panel-title">{t('streamer.queue.title')}</h2>
        <span className="cockpit-count-chip">
          {t('streamer.queue.songCount', { count: queuedSongs.length })}
        </span>
      </div>

      {notice && (
        <p className="cockpit-queue-notice" role="alert" data-testid="dashboard-queue-notice">{t(notice.key)}</p>
      )}

      <div className="cockpit-queue-tools">
        <label className="cockpit-search">
          <FaSearch className="cockpit-search-icon" aria-hidden="true" />
          <input
            type="search"
            className="cockpit-search-input"
            placeholder={t('streamer.queue.search')}
            aria-label={t('streamer.queue.search')}
            value={searchTerm}
            onChange={e => onSearchTermChange(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="cockpit-add-btn"
          onClick={onAddSong}
          data-testid="streamer-add-song"
          disabled={!canAddSong}
          aria-label={t('streamer.queue.addSong')}
          title={canAddSong ? t('streamer.queue.addSongTitle') : t('streamer.queue.addSongDenied')}
        >
          <FaPlus aria-hidden="true" />
        </button>
      </div>

      {upNext && (
        <div className="cockpit-upnext" data-testid="dashboard-queue-entry" data-entry-id={upNext.id}>
          <div className="cockpit-upnext-top">
            <span className="cockpit-eyebrow cockpit-eyebrow--blush">{t('streamer.queue.upNext')}</span>
            {canReorderQueue && (
              <ReorderControls
                song={upNext}
                canMoveUp={false}
                canMoveDown={queuedSongs.length > 1}
                onMove={onMoveQueueSong}
              />
            )}
            {removeButton(upNext)}
          </div>
          <div className="cockpit-upnext-main">
            <span className="cockpit-position cockpit-position--primary">1</span>
            <div className="cockpit-song-info">
              <span className="cockpit-upnext-title">{upNext.title}</span>
              <span className="cockpit-upnext-artist">{upNext.artist}</span>
            </div>
          </div>
          <DemandRow song={upNext} />
        </div>
      )}

      <div className="cockpit-queue-scroll">
        <div className="cockpit-queue-list" role="list" aria-label={t('streamer.queue.list')}>
          {queuedSongs.length === 0 && (
            <p className="cockpit-queue-empty">{t('streamer.queue.empty')}</p>
          )}
          {term && listSongs.length === 0 && queuedSongs.length > 0 && (
            <p className="cockpit-queue-empty">{t('streamer.queue.noMatches', { term: searchTerm.trim() })}</p>
          )}
          {listSongs.map((song) => {
            const queuedIndex = queuedSongs.findIndex(s => s.id === song.id);
            return (
              <div key={song.id} className="cockpit-queue-row" role="listitem" data-testid="dashboard-queue-entry" data-entry-id={song.id}>
                <span className="cockpit-position">{queuedIndex + 1}</span>
                <div className="cockpit-song-info">
                  <span className="cockpit-row-title">{song.title}</span>
                  <span className="cockpit-row-artist">{song.artist}</span>
                </div>
                <DemandRow song={song} className="cockpit-demand--row" />
                {canReorderQueue && (
                  <ReorderControls
                    song={song}
                    canMoveUp={queuedIndex > 0}
                    canMoveDown={queuedIndex < queuedSongs.length - 1}
                    onMove={onMoveQueueSong}
                  />
                )}
                {removeButton(song)}
              </div>
            );
          })}

          {playedSongs.length > 0 && (
            <div className="cockpit-played">
              <span className="cockpit-eyebrow">{t('streamer.queue.played')}</span>
              {playedSongs.map(song => (
                <div key={song.id} className="cockpit-queue-row cockpit-queue-row--played" role="listitem" data-testid="dashboard-played-entry" data-entry-id={song.id} data-status={song.status}>
                  <span className="cockpit-position cockpit-position--played" aria-hidden="true">
                    {song.status === 'skipped' ? <FaForward className="rtl-keep" /> : <FaCheck />}
                  </span>
                  <div className="cockpit-song-info">
                    <span className="cockpit-row-title">{song.title}</span>
                    <span className="cockpit-row-artist">
                      {song.status === 'skipped' ? t('streamer.queue.skippedArtist', { artist: song.artist }) : song.artist}
                    </span>
                  </div>
                  {canRequeue && (
                    <button
                      type="button"
                      className="cockpit-reorder-btn"
                      onClick={() => onRequeue(song.id)}
                      title={t('streamer.queue.requeueTitle')}
                      aria-label={t('streamer.queue.requeue', { title: song.title })}
                    >
                      <FaUndo className="rtl-mirror" aria-hidden="true" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="cockpit-scroll-fade" aria-hidden="true" />
      </div>

      <div className="cockpit-preview">
        <div className="cockpit-preview-header">
          <span className="cockpit-eyebrow">{t('streamer.queue.livePreview')}</span>
          {/* The badge sits beside the frame, never over it: anything painted
              on top of the embed makes Twitch fall back to click-to-play. */}
          {isLive && (
            <span className="cockpit-live-badge cockpit-live-badge--mini">
              <span className="cockpit-live-dot" aria-hidden="true" />
              {t('streamer.live')}
            </span>
          )}
        </div>
        <div className="cockpit-preview-video" data-testid="dashboard-preview-video">
          {/* Muted: this is the band's own stream, played back in the room
              they are performing in; unmuted it would echo into the mic.
              Unmounted while the fullscreen workspace hides it (ENG-844), so
              the embed isn't left playing unseen; it reloads on exit. */}
          {previewSuspended ? null : (
            <VideoPlayer
              bandId={bandid}
              isLoading={false}
              streamUrl={embedUrl ?? null}
              muted
              error={!isLive ? t('stream.player.offline') : null}
            />
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Performance workspace
// ---------------------------------------------------------------------------

const NowPerformingHeader = ({ song }) => {
  const { t, format } = useLocale();
  return (
    <div className="cockpit-now" aria-label={t('streamer.now.title')}>
      <div className="cockpit-now-identity">
        <span className="cockpit-eyebrow cockpit-eyebrow--blush">{t('streamer.now.title')}</span>
        {song ? (
          <>
            <div className="cockpit-now-title-row">
              <span className="cockpit-now-title">{song.title}</span>
              {song.artist && <span className="cockpit-now-artist">{song.artist}</span>}
            </div>
            {song.requestedBy && (
              <span className="cockpit-now-dedication">{t('streamer.now.requestedBy', { name: song.requestedBy })}</span>
            )}
          </>
        ) : (
          <>
            <div className="cockpit-now-title-row">
              <span className="cockpit-now-title">{t('streamer.now.noSong')}</span>
            </div>
            <span className="cockpit-now-dedication">{t('streamer.now.pressPlayNext')}</span>
          </>
        )}
      </div>
      {song && (
        <div className="cockpit-now-stats">
          <div className="cockpit-now-stat">
            <span className="cockpit-now-stat-value">{song.tempo || '—'}</span>
            <span className="cockpit-now-stat-label">{t('streamer.now.bpm')}</span>
          </div>
          <div className="cockpit-now-stat">
            <span className="cockpit-now-stat-value">
              {song.releaseDate ? String(song.releaseDate).slice(0, 4) : '—'}
            </span>
            <span className="cockpit-now-stat-label">{t('streamer.now.year')}</span>
          </div>
          <div className="cockpit-now-stat">
            <span className="cockpit-now-stat-value">
              {song.donationAmount > 0 ? formatMoney(song.donationAmount, format) : '—'}
            </span>
            <span className="cockpit-now-stat-label">{t('streamer.now.tip')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const PerformanceToolbar = ({
  keyLabel, capo, transpose, onTranspose, fontStep, onFontStep, showChords, onToggleChords,
  autoScroll, onToggleAutoScroll, scrollSpeed, onCycleScrollSpeed, isFullscreen, onToggleFullscreen,
}) => {
  const t = useT();
  return (
    <div className="cockpit-toolbar">
      <div className="cockpit-toolbar-group">
        <div className="cockpit-pill cockpit-pill--tight" role="group" aria-label={t('streamer.toolbar.transpose')}>
          <button
            type="button"
            className="cockpit-round-btn"
            onClick={() => onTranspose(-1)}
            aria-label={t('streamer.toolbar.transposeDown')}
            title={t('streamer.toolbar.transposeDownTitle')}
          >
            <FaMinus aria-hidden="true" />
          </button>
          <span className="cockpit-pill-readout">
            <span className="cockpit-pill-label">{t('streamer.toolbar.key')}</span>
            <span className="cockpit-pill-value">
              {keyLabel || '—'}
              {transpose !== 0 && (
                <span className="cockpit-pill-delta">{transpose > 0 ? `+${transpose}` : transpose}</span>
              )}
            </span>
          </span>
          <button
            type="button"
            className="cockpit-round-btn"
            onClick={() => onTranspose(1)}
            aria-label={t('streamer.toolbar.transposeUp')}
            title={t('streamer.toolbar.transposeUpTitle')}
          >
            <FaPlus aria-hidden="true" />
          </button>
        </div>

        <div className="cockpit-pill cockpit-pill--capo">
          <span className="cockpit-pill-label">{t('streamer.toolbar.capo')}</span>
          <span className="cockpit-pill-value">{capo || '—'}</span>
        </div>

        <div className="cockpit-pill cockpit-pill--tight" role="group" aria-label={t('streamer.toolbar.lyricSize')}>
          <button
            type="button"
            className="cockpit-round-btn cockpit-font-btn cockpit-font-btn--small"
            onClick={() => onFontStep(-1)}
            disabled={fontStep <= 0}
            aria-label={t('streamer.toolbar.smallerLyrics')}
          >
            {t('streamer.toolbar.smallerLyricsGlyph')}
          </button>
          <button
            type="button"
            className="cockpit-round-btn cockpit-font-btn"
            onClick={() => onFontStep(1)}
            disabled={fontStep >= LYRIC_FONT_SIZES.length - 1}
            aria-label={t('streamer.toolbar.largerLyrics')}
          >
            {t('streamer.toolbar.largerLyricsGlyph')}
          </button>
        </div>

        <button
          type="button"
          className="cockpit-pill cockpit-pill--toggle"
          onClick={onToggleChords}
          aria-pressed={showChords}
        >
          <span className="cockpit-pill-label">{t('streamer.toolbar.chords')}</span>
          <span className={`cockpit-switch ${showChords ? 'cockpit-switch--on' : ''}`.trim()} aria-hidden="true">
            <span className="cockpit-switch-knob" />
          </span>
        </button>
      </div>

      <div className="cockpit-toolbar-group">
        <div className="cockpit-pill cockpit-pill--tight cockpit-pill--scroll">
          <button
            type="button"
            className="cockpit-round-btn"
            onClick={onToggleAutoScroll}
            aria-pressed={autoScroll}
            aria-label={autoScroll ? t('streamer.toolbar.pauseAutoScroll') : t('streamer.toolbar.startAutoScroll')}
          >
            {autoScroll ? <FaPause aria-hidden="true" /> : <FaPlay aria-hidden="true" />}
          </button>
          <span className="cockpit-pill-readout">
            <span className="cockpit-pill-label">{t('streamer.toolbar.autoScroll')}</span>
            <span className="cockpit-pill-value cockpit-pill-value--state">
              <span
                className={`cockpit-state-dot ${autoScroll ? 'cockpit-state-dot--on' : ''}`.trim()}
                aria-hidden="true"
              />
              {autoScroll ? t('streamer.toolbar.on') : t('streamer.toolbar.off')}
            </span>
          </span>
          <button
            type="button"
            className="cockpit-speed-btn"
            onClick={onCycleScrollSpeed}
            aria-label={t('streamer.toolbar.scrollSpeed', { speed: scrollSpeed })}
          >
            {t('streamer.toolbar.scrollSpeedValue', { speed: scrollSpeed })}
            <FaChevronDown aria-hidden="true" />
          </button>
        </div>
        <button
          type="button"
          className="cockpit-round-btn cockpit-round-btn--lg"
          onClick={onToggleFullscreen}
          aria-pressed={isFullscreen}
          aria-label={isFullscreen ? t('streamer.toolbar.exitFullscreen') : t('streamer.toolbar.fullscreen')}
        >
          {isFullscreen ? <FaCompress aria-hidden="true" /> : <FaExpand aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
};

// One stand section's body: chord-over-lyric lines, or the performer notes /
// tabs text the song carries alongside its lyrics
const SectionBody = ({ section, showChords, transpose, accidentals }) => {
  if (section.kind === 'text') {
    return <p className="cockpit-lyric-text">{section.text}</p>;
  }
  if (section.kind === 'tabs') {
    return <pre className="cockpit-tabs">{section.text}</pre>;
  }
  return (
    <div className="cockpit-lyric-lines">
      {section.lines.map((segments, lineIndex) => {
        if (segments.length === 0) {
          return <div key={lineIndex} className="cockpit-lyric-break" aria-hidden="true" />;
        }
        const chordOnly = segments.every(seg => seg.lyric === '');
        return (
          <div
            key={lineIndex}
            className={`cockpit-lyric-line ${chordOnly ? 'cockpit-lyric-line--chords' : ''}`.trim()}
          >
            {segments.map((segment, segIndex) => (
              <span key={segIndex} className="cockpit-segment">
                {showChords && segment.chord && (
                  <span className="cockpit-chord">{transposeChord(segment.chord, transpose, accidentals)}</span>
                )}
                {segment.lyric !== '' && (
                  <span className="cockpit-lyric">{segment.lyric}</span>
                )}
              </span>
            ))}
          </div>
        );
      })}
    </div>
  );
};

/*
 * Musixmatch closes a licensed body with its own notice, blank-line separated
 * from the last stanza and sometimes followed by a bare tracking id in
 * parentheses. It has to be displayed — but it is the provider talking about
 * the licence, not a part of the song, so it is not given a number in the
 * section rail or a "Part N" heading over it.
 */
const NOTICE_LINE = /not\s+for\s+commercial\s+use/i;
const NOTICE_ID_LINE = /^\(\d+\)$/;
const isNoticeBlock = (section) =>
  section.lines.every(([segment]) => NOTICE_LINE.test(segment.lyric) || NOTICE_ID_LINE.test(segment.lyric.trim()));

/*
 * A provider lyrics body laid out as stand sections (ENG-415). Deliberately
 * not run through `parseLyricSheet`: these are not a band's chord sheet but
 * prose from Musixmatch, and the parser would read a line like "Bb" or
 * "Am I wrong" as chords — which `withoutChordOnlyLines` then drops from the
 * stand entirely. Every line arrives on screen exactly as it was sent, notice
 * and all (the "not for commercial use" sentence is part of what we are
 * licensed to show, not noise to tidy away); blank lines separate the sections
 * the rail jumps between, the same way an unlabelled saved sheet does.
 *
 * `provider: true` rides on each one, because what may be done with these
 * words differs from what may be done with the band's own: they carry the
 * track's language, the copy block for Japanese traffic applies to them and
 * to nothing else on the stand, and the credit below the stand is owed for
 * them being on it.
 */
const providerStandSections = (body, t, language = null) => {
  const blocks = [];
  let current = null;
  String(body)
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .forEach((rawLine) => {
      const line = rawLine.trimEnd();
      if (line.trim() === '') {
        current = null;
        return;
      }
      if (!current) {
        current = { lines: [] };
        blocks.push(current);
      }
      current.lines.push([{ chord: null, lyric: line }]);
    });

  // Only the trailing run is the licence notice; the same words earlier in a
  // body are somebody's lyric and are numbered like any other stanza.
  let noticeFrom = blocks.length;
  while (noticeFrom > 0 && isNoticeBlock(blocks[noticeFrom - 1])) noticeFrom -= 1;

  let partNumber = 0;
  return blocks.map((block, index) => {
    const footnote = index >= noticeFrom;
    if (!footnote) partNumber += 1;
    return {
      ...block,
      id: `provider-section-${index + 1}`,
      kind: 'lyric',
      provider: true,
      footnote,
      label: footnote ? t('streamer.stage.lyrics.notice') : t('lyricSheet.part', { number: partNumber }),
      // Third-party words in whatever language they were written in, not the
      // interface language: this is what tells a screen reader how to
      // pronounce them (WCAG 3.1.2). It goes on the words and not on the
      // sheet, which also holds our English and the provider's credit — nor
      // on the notice, which is the provider's own English.
      lang: footnote ? undefined : language || undefined,
    };
  });
};

/*
 * `note` is what happened to the lyrics read — still running, withdrawn,
 * refused — said out loud so the performer is not left to guess. It replaces
 * the stand's own "nothing saved" sentence on an empty stand and is appended
 * below whatever is on a stand that is not empty, because a band whose chord
 * chart is up and whose words were withdrawn is owed the reason just as much.
 *
 * `attribution` is the Musixmatch credit. It sits below the scrollport rather
 * than at the end of the sheet: the licence wants it visible whenever the
 * words are, and a performer reading verse three of a body that is eight
 * screens long at 36px never scrolls to the bottom of the sheet during the
 * song. `excerptBacklink` is the same obligation for a partial body.
 *
 * `blockCopying` is the Japanese-traffic obligation, and it is applied to the
 * provider's sections and not to the sheet: the band's own chart, and the
 * copyright line itself, stay copyable wherever the performer is.
 */
const LyricStage = ({
  sections, activeIndex, onSelectSection, sheetRef, showChords, transpose, accidentals, fontSize, hasSong,
  note = null, attribution = null, excerptBacklink = null, blockCopying = false,
}) => {
  const t = useT();
  const preventCopying = blockCopying ? (event) => event.preventDefault() : undefined;
  return (
    <div className="cockpit-stage">
      <nav className="cockpit-sections" aria-label={t('streamer.stage.sectionsNav')}>
        <span className="cockpit-eyebrow cockpit-sections-label">{t('streamer.stage.sections')}</span>
        {sections.map((section, index) => {
          // The provider's licence notice is shown but not navigated to: it
          // is not a part of the song, and numbering it in the rail says it is.
          if (section.footnote) return null;
          const state = index === activeIndex ? 'active' : index < activeIndex ? 'passed' : 'upcoming';
          return (
            <button
              key={section.id}
              type="button"
              className={`cockpit-section-btn cockpit-section-btn--${state}`}
              onClick={() => onSelectSection(index)}
              aria-current={state === 'active' ? 'true' : undefined}
            >
              {section.label}
            </button>
          );
        })}
      </nav>

      <div className="cockpit-sheet-scroll">
        <div className="cockpit-sheet-viewport">
          <div
            className="cockpit-sheet"
            ref={sheetRef}
            style={{ '--cockpit-lyric-size': `${fontSize}px` }}
            tabIndex={0}
            aria-label={t('streamer.stage.sheet')}
          >
            {sections.length === 0 && (
              <p className="cockpit-sheet-empty" role={note ? 'status' : undefined}>
                {note || (hasSong ? t('streamer.stage.noLyrics') : t('streamer.stage.noSong'))}
              </p>
            )}
            {sections.map((section, index) => {
              const state = index === activeIndex ? 'active' : index < activeIndex ? 'passed' : 'upcoming';
              const guarded = blockCopying && section.provider;
              return (
                <section
                  key={section.id}
                  className={[
                    'cockpit-lyric-section',
                    `cockpit-lyric-section--${state}`,
                    `cockpit-lyric-section--${section.kind}`,
                    section.footnote ? 'cockpit-lyric-section--footnote' : '',
                    guarded ? 'cockpit-lyric-section--no-copy' : '',
                  ].filter(Boolean).join(' ')}
                  // Footnotes are skipped by the rail and by the scroll spy,
                  // so they carry no index for either to land on.
                  data-section-index={section.footnote ? undefined : index}
                  aria-label={section.label}
                  lang={section.lang}
                  onCopy={guarded ? preventCopying : undefined}
                  onCut={guarded ? preventCopying : undefined}
                  onDragStart={guarded ? preventCopying : undefined}
                >
                  <span className="cockpit-lyric-rule" aria-hidden="true" />
                  <div className="cockpit-lyric-content">
                    {!section.footnote && <span className="cockpit-lyric-label">{section.label}</span>}
                    <SectionBody
                      section={section}
                      showChords={showChords}
                      transpose={transpose}
                      accidentals={accidentals}
                    />
                  </div>
                </section>
              );
            })}
            {/* Musixmatch's non-full-lyrics tiers serve roughly a third of a
                track. A performer who reaches the end of the fragment mid-song
                needs to know there is a rest of it, and the link to it is what
                makes showing the fragment lawful. */}
            {excerptBacklink && (
              <p className="cockpit-sheet-excerpt">
                <a href={excerptBacklink} target="_blank" rel="noopener noreferrer">
                  {t('streamer.stage.lyrics.excerpt')}
                </a>
              </p>
            )}
            {sections.length > 0 && note && (
              <p className="cockpit-sheet-note" role="status">{note}</p>
            )}
          </div>
          <div className="cockpit-scroll-fade cockpit-scroll-fade--sheet" aria-hidden="true" />
        </div>
        {attribution}
      </div>
    </div>
  );
};

// `isBusy` is a queue write in flight: the three buttons all move the queue,
// so a second click before the first lands would send duplicate writes.
const TransitionBar = ({ currentSong, nextSong, canControlPlayback, isBusy, onSkip, onFinish, onPlayNext }) => {
  const { t, format } = useLocale();
  return (
    <div className="cockpit-transition" aria-busy={isBusy || undefined}>
      <div className="cockpit-transition-nav">
        <button
          type="button"
          className="cockpit-ghost-btn"
          onClick={onSkip}
          disabled={!canControlPlayback || !currentSong || isBusy}
          title={t('streamer.transition.skipTitle')}
        >
          <FaForward className="rtl-keep" aria-hidden="true" />
          {t('streamer.transition.skip')}
        </button>
      </div>
      <div className="cockpit-transition-flow">
        <button
          type="button"
          className="cockpit-finish-btn"
          onClick={onFinish}
          disabled={!canControlPlayback || !currentSong || isBusy}
          title={t('streamer.transition.finishTitle')}
        >
          <FaCheck aria-hidden="true" />
          {t('streamer.transition.finish')}
        </button>
        <div className="cockpit-next-preview">
          <span className="cockpit-eyebrow">{t('common.actions.next')}</span>
          {nextSong ? (
            <span className="cockpit-next-row">
              <span className="cockpit-next-title">{nextSong.title}</span>
              {nextSong.artist && <span className="cockpit-next-artist">{nextSong.artist}</span>}
              {nextSong.donationAmount > 0 && (
                <>
                  <span className="cockpit-dot" aria-hidden="true">·</span>
                  <span className="cockpit-next-demand">
                    {t('streamer.transition.tip', { amount: formatMoney(nextSong.donationAmount, format) })}
                  </span>
                </>
              )}
            </span>
          ) : (
            <span className="cockpit-next-row">
              <span className="cockpit-next-artist">{t('streamer.transition.queueEmpty')}</span>
            </span>
          )}
        </div>
        <button
          type="button"
          className="cockpit-btn cockpit-btn--primary cockpit-play-next-btn"
          onClick={onPlayNext}
          disabled={!canControlPlayback || !nextSong || isBusy}
        >
          {t('streamer.transition.playNext')}
          <FaArrowRight className="rtl-mirror" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Audience center
// ---------------------------------------------------------------------------

// Audience support the dashboard actually knows about: the requests and tips
// attached to queue entries. Newest first (queue IDs grow with each request).
// `who` is null for a band pick and `finishedStatus` is set once the song has
// left the queue; ActivityFeed puts the words to both.
function queueActivity(queue) {
  return queue
    .filter(song => song.requestedBy || song.donationAmount > 0)
    .sort((a, b) => b.id - a.id)
    .map(song => ({
      id: song.id,
      who: song.requestedBy || null,
      amount: song.donationAmount,
      title: song.title,
      finishedStatus: song.status === 'played' || song.status === 'skipped' ? song.status : null,
    }));
}

const FINISHED_STATUS_KEYS = {
  played:  'streamer.activity.statusPlayed',
  skipped: 'streamer.activity.statusSkipped',
};

const ActivityFeed = ({ items }) => {
  const { t, format } = useLocale();
  const describe = (item) => {
    const what = item.finishedStatus
      ? t('streamer.activity.songWithStatus', { title: item.title, status: t(FINISHED_STATUS_KEYS[item.finishedStatus]) })
      : item.title;
    return item.amount > 0
      ? t('streamer.activity.tippedFor', { what })
      : t('streamer.activity.requested', { what });
  };
  return (
    <div className="cockpit-activity">
      <div className="cockpit-activity-list" role="list" aria-label={t('streamer.activity.list')}>
        {items.length === 0 && (
          <p className="cockpit-queue-empty">{t('streamer.activity.empty')}</p>
        )}
        {items.map(item => (
          <div key={item.id} className="cockpit-activity-item" role="listitem">
            <div className="cockpit-activity-top">
              <span className="cockpit-activity-donor">{item.who ?? t('streamer.activity.anonymous')}</span>
              {item.amount > 0 && <span className="cockpit-money-chip">{formatMoney(item.amount, format)}</span>}
            </div>
            <span className="cockpit-activity-message">{describe(item)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const StreamerDashboard = () => {
  const { bandid } = useParams();
  const navigate = useNavigate();
  const { t, format } = useLocale();
  // The queue fetchers read `t` through a ref so a locale switch does not
  // change their identity and re-run the band load that depends on them.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const [band, setBand] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Catalog key of the load error, rendered through `t`
  const [error, setError] = useState(null);

  // Narrow-viewport active panel: 'queue' | 'stage' | 'chat'
  const [activeTab, setActiveTab] = useState('stage');
  // Audience center tab: 'chat' | 'activity'
  const [audienceTab, setAudienceTab] = useState('chat');

  const fetchWrapperRef = useRef(useFetchWrapper());
  const memberRef = useRef(false);
  // memberRef as state, for the SongShake feed read (members only, ENG-763)
  const [isMember, setIsMember] = useState(false);
  // Whether the viewer may open OBS setup: members and the band's moderators,
  // whom the overlay token route admits (ENG-584).
  const obsSetupRef = useRef(false);

  // Real-time status from WebSocket hook (ENG-98). A queue_update on the same
  // socket refetches the queue: a listener's request or a change from
  // another tab shows up without a reload (ENG-461). fetchQueue is defined
  // further down, so it's read through a ref.
  const fetchQueueRef = useRef(null);
  const handleQueueUpdate = useCallback(() => {
    fetchQueueRef.current?.();
  }, []);
  // A donation_alert frame (ENG-577) shows as a short notice; the latest
  // replaces the one before it. Nothing sends one since ENG-596, when the
  // backend stopped receiving provider tips; it is kept for SongShake tips.
  const [donationNotice, setDonationNotice] = useState(null);
  const donationNoticeTimerRef = useRef(null);
  const handleDonationAlert = useCallback((frame) => {
    const notice = donationNoticeFrom(frame);
    if (!notice) return;
    if (donationNoticeTimerRef.current) clearTimeout(donationNoticeTimerRef.current);
    setDonationNotice(notice);
    donationNoticeTimerRef.current = setTimeout(() => setDonationNotice(null), DONATION_NOTICE_MS);
  }, []);
  useEffect(() => () => {
    if (donationNoticeTimerRef.current) clearTimeout(donationNoticeTimerRef.current);
  }, []);
  // A boost_alert frame (ENG-680) is noted for queue_song_completed's Boost
  // properties (ENG-888); nothing is shown for it here.
  const handleBoostAlert = useCallback((frame) => {
    noteBoostAlert(bandid, frame);
  }, [bandid]);
  const { isLive, viewerCount, connectionStatus, statusResolved, streamTitle, streamDescription, embedUrl } =
    useStreamStatus(bandid, { onQueueUpdate: handleQueueUpdate, onDonationAlert: handleDonationAlert, onBoostAlert: handleBoostAlert });
  // Whether SongShake is receiving the band's stream (ENG-763), re-read while
  // the dashboard is open. A drop mid-show is a warning, not an error: the
  // show carries on, only the songs played meanwhile get no replay clip.
  const { status: feedStatus } = useMultistreamStatus(bandid, { enabled: isMember });
  const feedDropped = isLive && isFeedDown(feedStatus);

  // Stream metadata (ENG-243)
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [isEditingMeta, setIsEditingMeta] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isSavingMeta, setIsSavingMeta] = useState(false);
  // `{ key }` of the catalog message to show under the form, or null
  const [saveMetaError, setSaveMetaError] = useState(null);
  // Tracks whether a WS show_metadata_update has already seeded metadata so the
  // REST read doesn't overwrite a fresher value (the status poll carries none)
  const metaInitializedRef = useRef(false);
  // The last title/description taken from the status hook. The sync effect
  // below applies a hook value only when it differs from this, so leaving edit
  // mode — which re-runs the effect while the hook still reports the pre-save
  // text — cannot roll a fresh save back. Only a newer socket value replaces
  // what was saved (ENG-342).
  const appliedStreamMetaRef = useRef({ title: null, description: null });

  const [queue, setQueue] = useState([]);
  const [queueSearch, setQueueSearch] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEndStreamModalOpen, setIsEndStreamModalOpen] = useState(false);
  // End Stream stays open until the show has ended, and says why when it
  // didn't (ENG-504).
  const [endStreamError, setEndStreamError] = useState(null);
  const [endingStream, setEndingStream] = useState(false);
  // A short, non-blocking message about the queue (a failed reorder):
  // { key } of a catalog message, cleared after a few seconds.
  const [queueNotice, setQueueNotice] = useState(null);
  // The queued entry awaiting a Remove confirmation (ENG-506), and why the
  // last removal failed.
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [removeError, setRemoveError] = useState(null);
  const [removing, setRemoving] = useState(false);
  const queueNoticeTimerRef = useRef(null);
  // Set once the backend says this queue's order is paid (ENG-676).
  const [queueOrderIsPaid, setQueueOrderIsPaid] = useState(false);
  const showQueueNotice = useCallback((key) => {
    if (queueNoticeTimerRef.current) clearTimeout(queueNoticeTimerRef.current);
    setQueueNotice({ key });
    queueNoticeTimerRef.current = setTimeout(() => setQueueNotice(null), QUEUE_NOTICE_MS);
  }, []);
  useEffect(() => () => {
    if (queueNoticeTimerRef.current) clearTimeout(queueNoticeTimerRef.current);
  }, []);
  const [canManageQueue, setCanManageQueue] = useState(false);
  const [canControlPlayback, setCanControlPlayback] = useState(false);
  // A queue-moving write (Skip / Finish / Play Next) is in flight
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [currentSongDetails, setCurrentSongDetails] = useState(null);
  // Whether the details read above has settled for the song now on stage
  // (either way — a refusal settles it too). "This song has no saved lyrics"
  // is only true once it has, and that is what decides whether to spend a
  // metered provider lookup on the song.
  const [songDetailsResolved, setSongDetailsResolved] = useState(false);
  // When the live show started, from the band's shows list; null until known,
  // in which case the header simply shows no start time or on-air duration.
  const [showStartedAt, setShowStartedAt] = useState(null);
  // The live show's id, from the same read, for the analytics events (ENG-888).
  const [liveShowId, setLiveShowId] = useState(null);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const currentSong = queue.find(s => s.status === 'playing') ?? null;
  const nextSong = useMemo(
    () => queue.filter(s => s.status === 'queued').sort((a, b) => a.position - b.position)[0] ?? null,
    [queue],
  );

  // Reading controls (ENG-359). Transpose and font size reset per song so a
  // shift made for one song never silently carries into the next.
  const [transpose, setTranspose] = useState(0);
  const [fontStep, setFontStep] = useState(DEFAULT_LYRIC_FONT_STEP);
  const [showChords, setShowChords] = useState(true);
  const [autoScroll, setAutoScroll] = useState(false);
  const [scrollSpeedIndex, setScrollSpeedIndex] = useState(SCROLL_SPEEDS.indexOf(1));
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const sheetRef = useRef(null);
  const workspaceRef = useRef(null);
  // The device's reduce-motion setting (ENG-837): section jumps scroll instantly.
  const reducedMotion = usePrefersReducedMotion();

  // The page owns the viewport background while mounted, like the band pages
  useEffect(() => {
    document.body.classList.add('band-page-body', 'cockpit-page-body');
    return () => document.body.classList.remove('band-page-body', 'cockpit-page-body');
  }, []);

  // Sync WS metadata updates to local display state (ENG-243). An update that
  // lands mid-edit is held back until the form closes so it cannot clobber
  // the fields being typed into.
  useEffect(() => {
    if (isEditingMeta) return;
    const applied = appliedStreamMetaRef.current;
    if (streamTitle != null && streamTitle !== applied.title) {
      applied.title = streamTitle;
      setMetaTitle(streamTitle);
      metaInitializedRef.current = true;
    }
    if (streamDescription != null && streamDescription !== applied.description) {
      applied.description = streamDescription;
      setMetaDescription(streamDescription);
      metaInitializedRef.current = true;
    }
  }, [streamTitle, streamDescription, isEditingMeta]);

  const handleStartEditMeta = () => {
    setEditTitle(metaTitle);
    setEditDescription(metaDescription);
    setSaveMetaError(null);
    setIsEditingMeta(true);
  };

  const handleCancelEditMeta = () => {
    setIsEditingMeta(false);
  };

  // The stream title and description belong to the band's live show (ENG-94):
  // `GET /public/bands/{id}/show/metadata` answers `{ showId, title,
  // description }` while a show is live and 404s otherwise. There is no
  // `/bands/{id}/stream` resource — reading and saving through it is what
  // 404'd the dashboard (ENG-342). Resolves to null when no show is live;
  // any other failure rejects so callers can tell "offline" from "broken".
  const fetchActiveShowMetadata = useCallback(async () => {
    try {
      const metadata = await fetchWrapperRef.current.get(
        `${config.apiBaseUrl}/public/bands/${bandid}/show/metadata`,
      );
      return metadata ?? null;
    } catch (err) {
      // 404 is the backend's "no active live show" answer, not a failure
      if (err?.status === 404) return null;
      throw err;
    }
  }, [bandid]);

  const handleSaveStreamMeta = async () => {
    const title = editTitle.trim();
    const description = editDescription.trim();
    if (!title) {
      setSaveMetaError({ key: 'streamer.showInfo.titleRequired' });
      return;
    }

    setIsSavingMeta(true);
    setSaveMetaError(null);
    try {
      // Edits are PATCHed against the live show, not the band, and the live
      // show can change under a mounted dashboard (a show ends and the next Go
      // Live starts a new one; nothing on the status socket announces the new
      // show's ID). Resolve it fresh on every save — one public GET — so a
      // remembered ID can never pin the dashboard to a show that has ended.
      const activeShow = await fetchActiveShowMetadata();
      if (activeShow?.showId == null) {
        setSaveMetaError({ key: 'streamer.showInfo.notLive' });
        return;
      }

      // Answers { showId, title, description } as stored; fall back to what
      // was sent should the body ever be empty.
      const saved = await fetchWrapperRef.current.patch(
        `${config.apiBaseUrl}/shows/${activeShow.showId}/metadata`,
        { title, description },
      );
      // Whatever the hook reported up to now predates this save, so treat it
      // as already applied: only a show_metadata_update that arrives after
      // (normally the echo of this very PATCH) may replace the saved text.
      appliedStreamMetaRef.current = { title: streamTitle, description: streamDescription };
      setMetaTitle(saved?.title ?? title);
      setMetaDescription(saved?.description ?? description);
      setIsEditingMeta(false);
    } catch (err) {
      console.error('Error saving stream metadata:', err);
      // 409: the show ended between the read above and the write; the next
      // save re-resolves, so the user only has to try again once it is live.
      setSaveMetaError({
        key: err?.status === 409 ? 'streamer.showInfo.notLive' : 'common.errors.saveFailed',
      });
    } finally {
      setIsSavingMeta(false);
    }
  };

  // Fetch full song details (key, tempo, lyrics, etc.) from the songs API
  // whenever the playing song changes. The previous song's details are dropped
  // as the song changes rather than when the next answer lands: holding them
  // would leave the last song's words under the new song's title for as long
  // as the read takes, and a slow answer for a song that has since been
  // skipped must not overwrite the one now on stage.
  useEffect(() => {
    setCurrentSongDetails(null);
    setSongDetailsResolved(false);
    if (!currentSong?.songId) return undefined;

    let cancelled = false;
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/bands/${bandid}/songs/${currentSong.songId}`)
      .then(data => {
        if (!cancelled) setCurrentSongDetails(data);
      })
      .catch(err => console.error('Error fetching song details:', err))
      .finally(() => {
        if (!cancelled) setSongDetailsResolved(true);
      });

    return () => {
      cancelled = true;
    };
  }, [currentSong?.songId, bandid]);

  // Reset the reading controls and scroll position for each new song
  useEffect(() => {
    setTranspose(0);
    setFontStep(DEFAULT_LYRIC_FONT_STEP);
    setActiveSectionIndex(0);
    setAutoScroll(false);
    if (sheetRef.current) sheetRef.current.scrollTop = 0;
  }, [currentSong?.id]);

  // Merge queue entry with full song details for the performance workspace
  const currentSongWithDetails = currentSong
    ? {
        ...currentSong,
        key:    currentSongDetails?.key    ?? currentSong.key,
        capo:   currentSongDetails?.capo   ?? currentSong.capo,
        tempo:  currentSongDetails?.tempo  ?? currentSong.tempo,
        chords: currentSongDetails?.chords ?? currentSong.chords,
        tabs:   currentSongDetails?.tabs   ?? currentSong.tabs,
        notes:  currentSongDetails?.notes  ?? currentSong.notes,
        lyrics: currentSongDetails?.lyrics ?? currentSong.lyrics,
      }
    : null;

  // The stand shows everything saved for the song: lyrics, the separate chord
  // chart, notes and tabs (see buildPerformanceSheet). With chords hidden the
  // chord-only lines go too, so the section rail matches what is on the sheet.
  const { lyrics: sheetLyrics, chords: sheetChords, tabs: sheetTabs, notes: sheetNotes } = currentSongWithDetails ?? {};
  const allSections = useMemo(
    () => buildPerformanceSheet({ lyrics: sheetLyrics, chords: sheetChords, tabs: sheetTabs, notes: sheetNotes }),
    [sheetLyrics, sheetChords, sheetTabs, sheetNotes],
  );
  const lyricSections = useMemo(
    () => (showChords ? allSections : withoutChordOnlyLines(allSections)),
    [allSections, showChords],
  );

  /*
   * On-demand lyrics for the song the performer just started (ENG-415).
   *
   * The stand shows what the band saved for a song, and most of a crate has
   * nothing saved: the words are then fetched from `GET /songs/{id}/lyrics`
   * (ENG-414), which reads them from Musixmatch at the moment they are asked
   * for and stores none of them, so no song, queue or crate payload carries
   * them and there is nowhere else to look. The read is keyed on the playing
   * entry's song id, so it follows the queue onto the next song and clears
   * with it.
   *
   * Only when the band saved none, and only once the details read that would
   * have carried them has settled: every lookup is metered, and a song the
   * band typed their own lyrics for has no need of one. That also keeps the
   * licence obligations off a stand showing words that are not the
   * provider's.
   *
   * "Saved none" is asked of the built sheet and not of the `lyrics` field,
   * because the field is not where a band's words necessarily are. A
   * chords-over-lyrics performance chart — the thing most bands actually
   * paste — goes in the `chords` field, and `buildPerformanceSheet` promotes
   * it to the body of the stand when the `lyrics` field carries no chart of
   * its own. Reading the raw field would spend a lookup that was not needed
   * and then print the same song twice, once as the band's chart and once as
   * the provider's prose, with a Musixmatch claim over the pair.
   */
  const hasSavedLyrics = useMemo(() => sheetHasSungText(allSections), [allSections]);
  // Deliberately does not read the lyrics feature flag: it gates listener surfaces only (Tim Withers, 2026-10-23, ENG-783); band-facing lyrics are never affected.
  const wantsProviderLyrics = Boolean(currentSong?.songId) && songDetailsResolved && !hasSavedLyrics;
  const providerLyrics = useSongLyrics(currentSong?.songId, { enabled: wantsProviderLyrics });

  // Whose words these are, decided by the backend's `source` and, for a value
  // it does not define, by evidence the payload carries: printing a Musixmatch
  // licence over a band's own text would be a false claim about someone
  // else's licence (see isProviderAnswer).
  const isProviderBody = isProviderAnswer(providerLyrics.lyrics, {
    restricted: providerLyrics.status === LyricsStatus.RESTRICTED,
  });

  const fetchedSections = useMemo(() => {
    if (providerLyrics.status !== LyricsStatus.READY) return [];
    // An excerpt with no link to the rest of the song is not shown at all
    // (ENG-521): the stand says there are no lyrics instead.
    if (isProviderBody && providerLyrics.lyrics?.partial && !providerLyrics.lyrics?.backlinkUrl) {
      console.warn('Lyrics excerpt arrived without a backlink; not showing it.', { songId: providerLyrics.lyrics?.songId ?? null });
      return [];
    }
    const body = typeof providerLyrics.lyrics?.body === 'string' ? providerLyrics.lyrics.body : '';
    return isProviderBody
      ? providerStandSections(body, t, providerLyrics.lyrics?.language)
      : withoutChordOnlyLines(parseLyricSheet(body, t));
  }, [providerLyrics.status, providerLyrics.lyrics, isProviderBody, t]);

  // The fetched words go after whatever else the band saved for the song (a
  // chord progression, notes, tabs), and only when nothing already on the
  // stand carries sung text — so the band's own words and the provider's are
  // never both up, which would be the same song printed twice.
  const standSections = hasSavedLyrics || fetchedSections.length === 0
    ? lyricSections
    : [...lyricSections, ...fetchedSections];

  /*
   * What happened to the lookup, said out loud. A performer who started a song
   * is owed the reason — the rights holder withdrew the words, the lookup is
   * still running, the provider is down — rather than the same "nothing saved
   * for this song" sentence in every case.
   *
   * Said whether or not the stand already has something on it. A band with a
   * chord progression saved and no words has a non-empty stand, and telling
   * them nothing at all about a withdrawal while printing Musixmatch's
   * copyright under their own chart is the worst of both. The stand renders
   * this as the empty-stand sentence when there is nothing else, and below the
   * sheet when there is.
   *
   * These are the performer's sentences, not the listener's: this page is
   * band-gated and mid-show, so "sign in to sing along" is not what happened
   * to anyone reading it.
   */
  const standNote = (() => {
    if (!currentSong || hasSavedLyrics) return null;
    if (!songDetailsResolved || providerLyrics.status === LyricsStatus.LOADING) {
      return t(lyricsKey('loading', LyricsVoice.PERFORMER));
    }
    if (providerLyrics.status === LyricsStatus.RESTRICTED) {
      return providerLyrics.lyrics?.restrictionMessage || t(lyricsKey('restricted', LyricsVoice.PERFORMER));
    }
    if (providerLyrics.status === LyricsStatus.UNAVAILABLE) {
      return t(unavailableKey(providerLyrics.code, LyricsVoice.PERFORMER));
    }
    // A 200 whose body parsed to nothing is the same news as a 404, and gets
    // the same sentence rather than an empty stand under a running song.
    if (providerLyrics.status === LyricsStatus.READY && fetchedSections.length === 0) {
      return t(unavailableKey('song_lyrics_not_found', LyricsVoice.PERFORMER));
    }
    return null;
  })();

  /*
   * The licence obligations, owed for anything of the provider's that is on
   * the stand — the words, and a withdrawal notice just as much, since that
   * arrives with the copyright and the backlink on it. Gated on what is
   * actually rendered and not on what was fetched: a copyright line under a
   * stand holding only the band's own chart claims someone else's licence
   * over the band's own work.
   *
   * The impression is the condition the words are served on, so it is
   * reported for the words only.
   */
  const showsProviderWords = isProviderBody && !hasSavedLyrics && fetchedSections.length > 0;
  // The same conditions `standNote` reaches its RESTRICTED branch under, so
  // the credit appears exactly when the withdrawal notice it is owed for does
  // — and never over the "looking up the words" sentence, which is ours.
  const showsProviderNotice =
    isProviderBody &&
    !hasSavedLyrics &&
    songDetailsResolved &&
    providerLyrics.status === LyricsStatus.RESTRICTED;
  const showsProviderAnswer = showsProviderWords || showsProviderNotice;
  useLyricsImpression(showsProviderWords ? providerLyrics.lyrics?.trackingScriptUrl ?? null : null, {
    pixelUrl: providerLyrics.lyrics?.trackingPixelUrl ?? null,
  });
  // An excerpt is lawful to show because the rest of it is one link away, so
  // the link is not decoration: without it a performer reads to the end of a
  // third of the song and finds out mid-verse.
  const excerptBacklink =
    showsProviderWords && providerLyrics.lyrics?.partial ? providerLyrics.lyrics?.backlinkUrl ?? null : null;
  // Read per render rather than once per module so a performer who crosses a
  // time zone mid-session is judged on where they are now. Only the provider's
  // words are covered: a band may copy their own lyrics wherever they are.
  const blockLyricCopying = showsProviderWords && isJapanTraffic();

  /*
   * The sections carry `onCopy`, which catches a copy made from inside them.
   * A document-level selection does not pass through them: Ctrl+A puts the
   * caret on `body` and the `copy` event is dispatched there, outside React's
   * subtree entirely. So the obligation is also enforced where that event
   * lands, and only for a selection that actually reaches the provider's
   * words — the band's own chart on the same stand stays copyable.
   */
  useEffect(() => {
    if (!blockLyricCopying) return undefined;
    const guard = (event) => {
      const selection = document.getSelection?.();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      const guarded = document.querySelectorAll('.cockpit-lyric-section--no-copy');
      const reaches = (node) =>
        typeof range.intersectsNode === 'function'
          ? range.intersectsNode(node)
          : selection.containsNode?.(node, true) ?? true;
      if ([...guarded].some(reaches)) event.preventDefault();
    };
    document.addEventListener('copy', guard);
    document.addEventListener('cut', guard);
    return () => {
      document.removeEventListener('copy', guard);
      document.removeEventListener('cut', guard);
    };
  }, [blockLyricCopying]);

  // Auto scroll: a requestAnimationFrame loop nudging the sheet down at the
  // chosen speed. It stops itself once the sheet has been scrolled to the
  // bottom; a sheet that fits without scrolling just leaves the setting on,
  // ready for when the lyrics grow past the panel.
  useEffect(() => {
    if (!autoScroll) return undefined;
    const sheet = sheetRef.current;
    if (!sheet) return undefined;
    let frame;
    let carry = 0;
    const pixelsPerFrame = 0.6 * SCROLL_SPEEDS[scrollSpeedIndex];
    const tick = () => {
      const scrollable = sheet.scrollHeight - sheet.clientHeight > 1;
      if (scrollable) {
        carry += pixelsPerFrame;
        const step = Math.floor(carry);
        if (step >= 1) {
          sheet.scrollTop += step;
          carry -= step;
        }
        if (sheet.scrollTop + sheet.clientHeight >= sheet.scrollHeight - 1) {
          setAutoScroll(false);
          return;
        }
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [autoScroll, scrollSpeedIndex]);

  // The on-air clock ticks once a second while the start time is known
  useEffect(() => {
    if (!showStartedAt) return undefined;
    setClockNow(Date.now());
    const interval = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [showStartedAt]);

  // Highlight the section nearest the top of the sheet as it scrolls
  const handleSheetScroll = useCallback(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    const nodes = sheet.querySelectorAll('[data-section-index]');
    const threshold = sheet.scrollTop + sheet.clientHeight * 0.25;
    let index = 0;
    nodes.forEach((node) => {
      if (node.offsetTop <= threshold) index = Number(node.dataset.sectionIndex);
    });
    setActiveSectionIndex(index);
  }, []);

  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return undefined;
    sheet.addEventListener('scroll', handleSheetScroll, { passive: true });
    return () => sheet.removeEventListener('scroll', handleSheetScroll);
  }, [handleSheetScroll, standSections.length]);

  const handleSelectSection = (index) => {
    const sheet = sheetRef.current;
    const node = sheet?.querySelector(`[data-section-index="${index}"]`);
    if (sheet && node) {
      // .cockpit-sheet is positioned, so offsetTop is already relative to it
      sheet.scrollTo({ top: node.offsetTop, behavior: reducedMotion ? 'instant' : 'smooth' });
    }
    setActiveSectionIndex(index);
  };

  // Fullscreen follows the browser's own state so Esc leaves cleanly; where
  // the API is unavailable the workspace simply pins itself to the viewport.
  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const handleToggleFullscreen = async () => {
    const node = workspaceRef.current;
    if (node?.requestFullscreen && document.fullscreenEnabled) {
      try {
        if (document.fullscreenElement) await document.exitFullscreen();
        else await node.requestFullscreen();
        return;
      } catch (err) {
        console.error('Fullscreen unavailable:', err);
      }
    }
    setIsFullscreen(prev => !prev);
  };

  const fetchQueue = useCallback(async () => {
    try {
      const data = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/queue/${bandid}/songs`);
      const formatted = (data || []).map(item => formatQueueItem(item, tRef.current));
      setQueue(formatted);
      noteQueueEntries(bandid, formatted);
    } catch (err) {
      console.error('Error fetching queue:', err);
    }
  }, [bandid]);
  fetchQueueRef.current = fetchQueue;

  const checkQueueAccessAsModerator = useCallback(async () => {
    const token = sessionStorage.getItem('jwt');
    if (!token) return false;

    try {
      const response = await fetch(`${config.apiBaseUrl}/queue/${bandid}/songs`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      const formatted = (data || []).map(item => formatQueueItem(item, tRef.current));
      setQueue(formatted);
      noteQueueEntries(bandid, formatted);
      return true;
    } catch (err) {
      console.error('Error checking moderator queue access:', err);
      return false;
    }
  }, [bandid]);

  // Finish Song, Skip and Play Next are one atomic write (ENG-505):
  // POST /queue/{bandId}/advance finishes the playing entry, renumbers the
  // queue and starts the next one in a single transaction, and only if the
  // entry playing is the one this dashboard shows (ENG-490). It used to be
  // three writes, so a failure part-way could leave nothing playing while
  // the dashboard still showed the old song. Either way the queue is read
  // back afterwards, so the dashboard shows what the server has.
  const advanceQueue = async (expectedEntry, outcome) => {
    if (isAdvancing) return;
    setIsAdvancing(true);
    // Read before the advance, while the entry is still in the queue as noted.
    const completed = expectedEntry && outcome === 'played' ? completionProperties(bandid, expectedEntry) : null;
    try {
      await fetchWrapperRef.current.post(`${config.apiBaseUrl}/queue/${parseInt(bandid, 10)}/advance`, {
        expectedCurrentEntryId: expectedEntry ? expectedEntry.id : null,
        outcome,
      });
      // Songs played (ENG-888): once the server has marked the entry played.
      if (completed) {
        void capturePerformerEvent('queue_song_completed', {
          bandId: bandid,
          fetchWrapper: fetchWrapperRef.current,
          show: isLive && liveShowId ? { show_live: true, show_id: liveShowId } : null,
          ...completed,
        });
      }
    } catch (err) {
      console.error('Error advancing queue:', err);
      // 409: someone else advanced first, or this view was stale. Show the
      // real queue rather than retrying.
      showQueueNotice(err?.status === 409 ? 'streamer.queue.advanceConflict' : 'streamer.queue.advanceFailed');
    } finally {
      await fetchQueue();
      setIsAdvancing(false);
    }
  };

  const handleAdvance = async (finishStatus) => {
    const playing = queue.find(s => s.status === 'playing');
    if (!playing) return;
    await advanceQueue(playing, finishStatus === SongStatus.SKIPPED ? 'skipped' : 'played');
  };

  // Starts the first queued song when nothing is playing (the Play Next
  // button); with a song playing it finishes that one and advances instead.
  const handlePlayNext = async () => {
    if (currentSong) {
      await handleAdvance(SongStatus.PLAYED);
      return;
    }
    if (!nextSong) return;
    await advanceQueue(null, 'played');
  };

  const handleRemoveRequest = (song) => {
    setRemoveError(null);
    setPendingRemoval(song);
  };

  // Deletes exactly the confirmed entry, by its ID (ENG-506).
  const confirmRemove = async () => {
    if (!pendingRemoval || removing) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await fetchWrapperRef.current.delete(`${config.apiBaseUrl}/queue/${parseInt(bandid, 10)}/songs/${pendingRemoval.id}`);
      setPendingRemoval(null);
    } catch (err) {
      console.error('Error removing queue entry:', err);
      setRemoveError(serverMessage(err) || t('streamer.queue.removeFailed'));
    } finally {
      setRemoving(false);
      await fetchQueue();
    }
  };

  const handleRequeue = async (requeueId) => {
    const song = queue.find(s => s.id === requeueId);
    if (!song) return;
    const newPosition = queue.filter(s => s.status === 'playing' || s.status === 'queued').length + 1;
    try {
      await fetchWrapperRef.current.post(`${config.apiBaseUrl}/queue/songstatus`, {
        requestId: song.id,
        position:  newPosition,
        bandId:    parseInt(bandid, 10),
        status:    SongStatus.QUEUED,
      });
      await fetchQueue();
    } catch (err) {
      // Said on the dashboard like the other queue actions (ENG-579), and the
      // queue is read back so the rail shows what the server has.
      console.error('Error re-queuing song:', err);
      showQueueNotice('streamer.queue.requeueFailed');
      await fetchQueue();
    }
  };

  // Move up / Move down act on the queued songs the rail lists, in the order
  // it lists them. The playing song is not in that list and is never touched:
  // it stays pinned at position 1 of the reorder payload, so a reorder can
  // never change which song is on the stand.
  const handleMoveQueueSong = async (queueItemId, direction) => {
    if (queueOrderIsPaid) return;
    const playingSong = queue.find(s => s.status === 'playing') || null;
    const queuedSongs = queue
      .filter(s => s.status === 'queued')
      .sort((a, b) => a.position - b.position);
    const completedSongs = queue.filter(s => s.status !== 'playing' && s.status !== 'queued');

    const currentIndex = queuedSongs.findIndex(song => song.id === queueItemId);
    if (currentIndex < 0) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= queuedSongs.length) return;

    const reorderedQueued = [...queuedSongs];
    [reorderedQueued[currentIndex], reorderedQueued[targetIndex]] = [reorderedQueued[targetIndex], reorderedQueued[currentIndex]];

    const activeSongs = (playingSong ? [playingSong, ...reorderedQueued] : reorderedQueued)
      .map((song, index) => ({ ...song, position: index + 1 }));

    // Update UI immediately so the rail reflects the new order without waiting for the API round-trip.
    setQueue([...activeSongs, ...completedSongs]);

    try {
      await fetchWrapperRef.current.put(`${config.apiBaseUrl}/queue/reorder`, {
        bandId: parseInt(bandid, 10),
        queuedSongs: activeSongs.map(song => ({
          ID: song.id,
          Position: song.position,
          RequestedBy: song.requestedBy || '',
          DonationAmount: song.donationAmount || 0,
        })),
      });
      await fetchQueue();
    } catch (err) {
      await fetchQueue();
      // A paid queue plays in the order listeners paid for and refuses a
      // manual reorder (ENG-676): say so, and stop offering the controls.
      if (err?.code === 'paid_queue_order') {
        setQueueOrderIsPaid(true);
        showQueueNotice('streamer.queue.paidOrder');
        return;
      }
      console.error('Error reordering queue:', err);
      showQueueNotice('streamer.queue.reorderFailed');
    }
  };

  const handleMarkPlayed = () => handleAdvance(SongStatus.PLAYED);
  const handleSkip = () => handleAdvance(SongStatus.SKIPPED);

  const handleEndStream = () => {
    setEndStreamError(null);
    setIsEndStreamModalOpen(true);
  };

  // The modal stays open, and the dashboard stays live, until the show has
  // actually ended (ENG-504); it used to close first and only log a failure.
  const confirmEndStream = async () => {
    if (endingStream) return;
    setEndingStream(true);
    setEndStreamError(null);

    try {
      const shows = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/bands/${bandid}/shows`);
      const activeShow = findActiveShow(shows);

      const activeShowId = activeShow?.id ?? activeShow?.ID;
      if (!activeShowId) {
        setEndStreamError(t('streamer.endStreamModal.noActiveShow'));
        return;
      }

      await fetchWrapperRef.current.post(`${config.apiBaseUrl}/shows/${activeShowId}/end`, {});
      // ENG-888: only once the show has ended; show_live is the state after.
      void capturePerformerEvent('end_show_clicked', {
        bandId: bandid,
        show: { show_live: false, show_id: Number(activeShowId) },
      });
      setIsEndStreamModalOpen(false);
      // Straight to the show's digest (ENG-446), which says it is being put
      // together until the pipeline finishes.
      navigate(`/performers/${bandid}/shows/${activeShowId}/report`, { replace: true });
    } catch (err) {
      console.error('Error ending stream:', err);
      setEndStreamError(serverMessage(err) || t('streamer.endStreamModal.failed'));
    } finally {
      setEndingStream(false);
    }
  };

  // No donation_amount (ENG-576): the backend never honours a client-sent
  // one (ENG-487), so the form no longer offers a field that did nothing.
  // Paid boosts are ENG-444 and ENG-445.
  const handleAddSong = async (newSong) => {
    const requestBody = {
      band_id: parseInt(bandid, 10),
      title: newSong.Title,
      artist: newSong.Artist,
      requested_by: newSong.RequestedBy || '',
      position: 0,
    };

    // A failure is left to reject, so the modal shows it and keeps the
    // form (ENG-504); swallowing it here left the modal open with no word.
    await fetchWrapperRef.current.post(`${config.apiBaseUrl}/queue`, requestBody);
    setIsAddModalOpen(false);
    await fetchQueue();
  };

  useEffect(() => {
    const fetchBand = async () => {
      try {
        setIsLoading(true);
        const bandData = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/bands/${bandid}`);
        setBand(bandData);
        // viewer_role (ENG-494) is the role source; a backend that predates
        // it still lists the members.
        memberRef.current = bandData?.viewer_role
          ? bandData.viewer_role === 'member'
          : isBandMember(bandData, getCurrentUserId());
        setIsMember(memberRef.current);
        // OBS setup is for members and moderators (ENG-584). Without
        // viewer_role only the members list is known, so only members get it.
        obsSetupRef.current = bandData?.viewer_role
          ? bandData.viewer_role === 'member' || bandData.viewer_role === 'moderator'
          : memberRef.current;

        const fetchInitialStreamMeta = async () => {
          let streamMeta = null;
          try {
            streamMeta = await fetchActiveShowMetadata();
          } catch (err) {
            // The panel is not worth blocking the dashboard for; it stays empty
            console.error('Error fetching live show metadata:', err);
          }
          // Skip if the WS snapshot already provided fresher values during the await
          if (metaInitializedRef.current) return;
          if (streamMeta?.title != null) setMetaTitle(streamMeta.title);
          if (streamMeta?.description != null) setMetaDescription(streamMeta.description);
        };

        if (memberRef.current) {
          setCanManageQueue(true);
          setCanControlPlayback(true);
          await fetchInitialStreamMeta();
          fetchQueue();
          return;
        }

        const hasModeratorAccess = await checkQueueAccessAsModerator();
        if (hasModeratorAccess) {
          setCanManageQueue(true);
          setCanControlPlayback(false);
          await fetchInitialStreamMeta();
          return;
        }

        navigate(`/performers/${bandid}/stream`, { replace: true });
      } catch (err) {
        console.error('Error loading band data:', err);
        setError('common.errors.loadBand');
      } finally {
        setIsLoading(false);
      }
    };

    if (bandid) fetchBand();
  }, [bandid, navigate, fetchQueue, checkQueueAccessAsModerator, fetchActiveShowMetadata]);

  useEffect(() => {
    if (isLoading || !band) return;
    if (!canManageQueue && !canControlPlayback) return;
    if (!statusResolved) return;
    if (isLive) return;

    navigate(`/performers/${bandid}/stream`, { replace: true });
  }, [isLoading, band, canManageQueue, canControlPlayback, statusResolved, isLive, bandid, navigate]);

  // The live show's start time drives "started HH:MM" and the on-air clock.
  // It comes from the band's shows list once the dashboard knows the band is
  // live; if the list cannot be read (a queue moderator may not be allowed to)
  // both simply stay hidden rather than showing a made-up time.
  useEffect(() => {
    if (isLoading || !band || !isLive) {
      setShowStartedAt(null);
      setLiveShowId(null);
      return undefined;
    }
    let cancelled = false;
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/bands/${bandid}/shows`)
      .then((shows) => {
        if (cancelled) return;
        const activeShow = findActiveShow(shows);
        setShowStartedAt(activeShow?.startedAt ?? activeShow?.StartedAt ?? null);
        const showId = Number(activeShow?.id ?? activeShow?.ID);
        setLiveShowId(Number.isSafeInteger(showId) && showId > 0 ? showId : null);
      })
      .catch((err) => {
        if (!cancelled) console.error('Error reading the live show start time:', err);
      });
    return () => { cancelled = true; };
  }, [isLoading, band, isLive, bandid]);

  const handleGoBack = () => navigate(`/performers/${bandid}`);

  // ------------------------------------------------------------------
  // Loading / error states
  // ------------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="streamer-dashboard">
        <div className="streamer-dashboard-header">
          <TopPageNavigationBand showHamburgerMenu={false} />
        </div>
        <div className="streamer-dashboard-loading" aria-live="polite" data-testid="streamer-dashboard-loading">
          <p>{t('streamer.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !band) {
    return (
      <div className="streamer-dashboard">
        <div className="streamer-dashboard-header">
          <TopPageNavigationBand showHamburgerMenu={false} />
        </div>
        <div className="streamer-dashboard-error" data-testid="streamer-dashboard-error">
          <p>{t(error || 'common.errors.bandNotFound')}</p>
          <button className="cockpit-btn cockpit-btn--outline" onClick={handleGoBack}>
            <FaArrowLeft className="rtl-mirror" aria-hidden="true" /> {t('common.actions.back')}
          </button>
        </div>
      </div>
    );
  }

  const canEditStreamMeta = memberRef.current;

  const wsBadgeClass = `streamer-ws-badge--${WS_BADGE_KEYS[connectionStatus] ? connectionStatus : 'unknown'}`;

  const wsBadgeLabel = t(WS_BADGE_KEYS[connectionStatus] || 'common.status.unknown');

  // Tips the dashboard knows about: those attached to queue requests
  const tipTotal = queue.reduce((sum, song) => sum + (Number(song.donationAmount) || 0), 0);
  const activityItems = queueActivity(queue);
  const startedAt = isLive ? showStartedAt : null;
  const displayKey = currentSongWithDetails?.key
    ? transposeKey(currentSongWithDetails.key, transpose)
    : null;
  // Spell the chords the way the (transposed) key is spelled: flats under Bb, sharps under F#
  const chordAccidentals = accidentalsForKey(displayKey);

  const menuItems = [
    { labelKey: 'streamer.menu.bandPage', onSelect: handleGoBack },
    { labelKey: 'navigation.songs',       onSelect: () => navigate(`/performers/${bandid}/song-list`) },
    { labelKey: 'navigation.events',      onSelect: () => navigate(`/performers/${bandid}/events`) },
    { labelKey: 'streamer.menu.home',     onSelect: () => navigate('/home') },
  ];

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <div className="streamer-dashboard cockpit" data-testid="streamer-dashboard">
      {/* Always mounted so screen readers announce the tip when it appears */}
      <div className="cockpit-donation-live" role="status" aria-live="polite">
        {donationNotice && (
          <p className="cockpit-donation-notice" data-testid="dashboard-donation-notice">
            <FaDollarSign aria-hidden="true" />
            <span>
              {t('streamer.donation.tipped', {
                name: donationNotice.name || t('streamer.donation.anonymous'),
                amount: formatDonationAmount(donationNotice, format),
              })}
            </span>
          </p>
        )}
      </div>
      {/* ---- Live session header ---- */}
      <header className="cockpit-header">
        <div className="cockpit-identity">
          <Logo size="small" />
          <span className="cockpit-identity-divider" aria-hidden="true" />
          {isLive && (
            <span className="cockpit-live-badge" data-testid="streamer-live-badge">
              <span className="cockpit-live-dot" aria-hidden="true" />
              {t('streamer.live')}
            </span>
          )}
          <ShowInfo
            bandName={band.Name ?? t('streamer.bandFallbackName')}
            startedAt={startedAt}
            title={metaTitle}
            description={metaDescription}
            isEditing={isEditingMeta}
            editTitle={editTitle}
            editDescription={editDescription}
            onEdit={handleStartEditMeta}
            onSave={handleSaveStreamMeta}
            onCancel={handleCancelEditMeta}
            onEditTitleChange={setEditTitle}
            onEditDescriptionChange={setEditDescription}
            canEdit={canEditStreamMeta}
            isSaving={isSavingMeta}
            saveError={saveMetaError}
          />
        </div>

        <div className="cockpit-metrics">
          <MultistreamFeedIndicator status={feedStatus} className="cockpit-feed-indicator" />
          <SessionMetric icon={<FaEye />} value={format.number(viewerCount)} label={t('streamer.metrics.watching')} />
          {startedAt && (
            <SessionMetric icon={<FaClock />} value={formatDuration(startedAt, clockNow)} label={t('streamer.metrics.onAir')} />
          )}
          <SessionMetric icon={<FaDollarSign />} value={formatMoney(tipTotal, format)} label={t('streamer.metrics.tips')} />
          <SessionMetric
            className={`streamer-ws-badge ${wsBadgeClass}`}
            ariaLabel={t('streamer.metrics.socketStatus', { status: wsBadgeLabel })}
            icon={<span className="streamer-ws-dot" />}
            value={wsBadgeLabel}
            label={t('streamer.metrics.socket')}
          />
        </div>

        <div className="cockpit-actions">
          {/* OBS setup is for members and the band's moderators (ENG-584; members only since ENG-501). */}
          {obsSetupRef.current && (
            <button
              type="button"
              className="cockpit-btn cockpit-btn--outline"
              onClick={() => navigate(`/performers/${bandid}/obs-setup`)}
              title={t('streamer.actions.streamSettingsTitle')}
              aria-label={t('streamer.actions.streamSettingsLabel')}
            >
              {t('streamer.actions.streamSettings')}
            </button>
          )}
          {isLive && canControlPlayback && (
            <button
              type="button"
              className="cockpit-btn cockpit-btn--danger"
              onClick={handleEndStream}
              data-testid="streamer-end-stream"
              title={t('streamer.actions.endStreamTitle')}
            >
              {t('streamer.actions.endStream')}
            </button>
          )}
          <CockpitMenu items={menuItems} />
        </div>
      </header>

      {feedDropped && (
        <div className="cockpit-feed-warning" role="status" data-testid="streamer-feed-drop-warning">
          <span>{t('streamer.feedDrop.warning')}</span>
          <button
            type="button"
            className="cockpit-btn cockpit-btn--outline"
            onClick={() => navigate(`/performers/${bandid}`, { state: { openView: 'songshake-feed' } })}
            data-testid="streamer-feed-drop-fix"
          >
            {t('streamer.feedDrop.fix')}
          </button>
        </div>
      )}

      {/* ---- Narrow-viewport panel switcher ---- */}
      <nav className="cockpit-tab-bar" role="tablist" aria-label={t('streamer.tabs.sections')}>
        {[
          { id: 'queue', icon: <FaList aria-hidden="true" />,    label: t('streamer.tabs.queue') },
          { id: 'stage', icon: <FaMusic aria-hidden="true" />,   label: t('streamer.tabs.stage') },
          { id: 'chat',  icon: <FaComment aria-hidden="true" />, label: t('streamer.tabs.chat') },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`cockpit-tab-${tab.id}`}
            aria-selected={activeTab === tab.id}
            aria-controls={`cockpit-panel-${tab.id}`}
            className={`cockpit-tab-btn ${activeTab === tab.id ? 'cockpit-tab-btn--active' : ''}`.trim()}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>

      {/* ---- Cockpit body ---- */}
      <main className="cockpit-body" aria-label={t('streamer.panels.main')}>
        <aside
          id="cockpit-panel-queue"
          className={`cockpit-panel cockpit-queue-rail ${activeTab === 'queue' ? 'cockpit-panel--tab-active' : ''}`.trim()}
          aria-label={t('streamer.panels.queue')}
        >
          <QueueRail
            bandid={bandid}
            isLive={isLive}
            embedUrl={embedUrl}
            queue={queue}
            searchTerm={queueSearch}
            onSearchTermChange={setQueueSearch}
            onRequeue={handleRequeue}
            onMoveQueueSong={handleMoveQueueSong}
            canReorderQueue={canManageQueue && !queueOrderIsPaid}
            canRequeue={canControlPlayback}
            canAddSong={canManageQueue}
            onAddSong={() => setIsAddModalOpen(true)}
            notice={queueNotice}
            onRemove={handleRemoveRequest}
            canRemove={canManageQueue}
            previewSuspended={isFullscreen}
          />
        </aside>

        <section
          ref={workspaceRef}
          id="cockpit-panel-stage"
          className={[
            'cockpit-panel',
            'cockpit-workspace',
            activeTab === 'stage' ? 'cockpit-panel--tab-active' : '',
            isFullscreen ? 'cockpit-workspace--fullscreen' : '',
          ].filter(Boolean).join(' ')}
          aria-label={t('streamer.panels.workspace')}
        >
          <NowPerformingHeader song={currentSongWithDetails} />
          <PerformanceToolbar
            keyLabel={displayKey}
            capo={currentSongWithDetails?.capo}
            transpose={transpose}
            onTranspose={(delta) => setTranspose(t => Math.max(-11, Math.min(11, t + delta)))}
            fontStep={fontStep}
            onFontStep={(delta) => setFontStep(s => Math.max(0, Math.min(LYRIC_FONT_SIZES.length - 1, s + delta)))}
            showChords={showChords}
            onToggleChords={() => setShowChords(v => !v)}
            autoScroll={autoScroll}
            onToggleAutoScroll={() => setAutoScroll(v => !v)}
            scrollSpeed={SCROLL_SPEEDS[scrollSpeedIndex]}
            onCycleScrollSpeed={() => setScrollSpeedIndex(i => (i + 1) % SCROLL_SPEEDS.length)}
            isFullscreen={isFullscreen}
            onToggleFullscreen={handleToggleFullscreen}
          />
          <div className="cockpit-divider" aria-hidden="true" />
          <LyricStage
            sections={standSections}
            activeIndex={activeSectionIndex}
            onSelectSection={handleSelectSection}
            sheetRef={sheetRef}
            showChords={showChords}
            transpose={transpose}
            accidentals={chordAccidentals}
            fontSize={LYRIC_FONT_SIZES[fontStep]}
            hasSong={Boolean(currentSong)}
            note={standNote}
            excerptBacklink={excerptBacklink}
            blockCopying={blockLyricCopying}
            attribution={
              showsProviderAnswer ? (
                <LyricsAttribution
                  copyright={providerLyrics.lyrics?.copyright}
                  backlinkUrl={providerLyrics.lyrics?.backlinkUrl}
                  variant="cockpit-lyrics"
                />
              ) : null
            }
          />
          <div className="cockpit-divider" aria-hidden="true" />
          <TransitionBar
            currentSong={currentSong}
            nextSong={nextSong}
            canControlPlayback={canControlPlayback}
            isBusy={isAdvancing}
            onSkip={handleSkip}
            onFinish={handleMarkPlayed}
            onPlayNext={handlePlayNext}
          />
        </section>

        <aside
          id="cockpit-panel-chat"
          className={`cockpit-panel cockpit-audience ${activeTab === 'chat' ? 'cockpit-panel--tab-active' : ''}`.trim()}
          aria-label={t('streamer.panels.audience')}
        >
          <div className="cockpit-audience-tabs" role="tablist" aria-label={t('streamer.panels.audience')}>
            {[
              { id: 'chat',     label: t('streamer.tabs.chat') },
              { id: 'activity', label: t('streamer.tabs.activity') },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`cockpit-audience-tab-${tab.id}`}
                aria-selected={audienceTab === tab.id}
                aria-controls={`cockpit-audience-pane-${tab.id}`}
                className={`cockpit-audience-tab ${audienceTab === tab.id ? 'cockpit-audience-tab--active' : ''}`.trim()}
                onClick={() => setAudienceTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* Band members and queue moderators both moderate chat; anyone
              else has already been redirected to the public stream page. */}
          <div
            id="cockpit-audience-pane-chat"
            role="tabpanel"
            aria-labelledby="cockpit-audience-tab-chat"
            hidden={audienceTab !== 'chat'}
            className={`cockpit-audience-pane ${audienceTab === 'chat' ? '' : 'cockpit-audience-pane--hidden'}`.trim()}
          >
            <ChatSidebar bandId={bandid} isVisible={true} isLive={isLive} canModerate={canManageQueue} variant="cockpit" />
          </div>
          {audienceTab === 'activity' && (
            <div
              id="cockpit-audience-pane-activity"
              role="tabpanel"
              aria-labelledby="cockpit-audience-tab-activity"
              className="cockpit-audience-pane"
            >
              <ActivityFeed items={activityItems} />
            </div>
          )}
        </aside>
      </main>

      <AddToQueueModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddSong}
        initialValues={ADD_TO_QUEUE_DEFAULTS}
      />

      <ConfirmationModal
        isOpen={Boolean(pendingRemoval)}
        onClose={() => setPendingRemoval(null)}
        onConfirm={confirmRemove}
        confirmTestId="streamer-remove-confirm"
        title={t('streamer.queue.removeModalTitle')}
        message={pendingRemoval ? t('streamer.queue.removeModalMessage', { title: pendingRemoval.title }) : ''}
        confirmText={t('streamer.queue.removeConfirm')}
        cancelText={t('common.actions.cancel')}
        isDangerous={true}
        error={removeError}
        isBusy={removing}
      />

      <ConfirmationModal
        isOpen={isEndStreamModalOpen}
        onClose={() => setIsEndStreamModalOpen(false)}
        onConfirm={confirmEndStream}
        error={endStreamError}
        isBusy={endingStream}
        confirmTestId="streamer-end-stream-confirm"
        title={t('streamer.endStreamModal.title')}
        message={t('streamer.endStreamModal.message')}
        confirmText={t('streamer.actions.endStream')}
        cancelText={t('common.actions.cancel')}
        isDangerous={true}
      />
    </div>
  );
};

export default StreamerDashboard;
